import api from './api';



interface PaymentAccountStatus {
  success: boolean;
  message: string;
  data: {
  payment_account: any;
  merchant_status?: string;
  processing_enabled?: boolean;
  settlement_enabled?: boolean;
  verification_status?: any;
    required_actions?: string[];
    error?: string;
  };
}

export const clinicPaymentService = {

  // Get payment account status
  async getPaymentAccountStatus(clinicId: string): Promise<PaymentAccountStatus | null> {
    try {
      const response = await api.get<PaymentAccountStatus>(
        `/clinic/${clinicId}/payment-config`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get payment account status:', error);
      return null;
    }
  },

  async refreshStripeOnboardingLink(clinicId: string): Promise<{ onboardingUrl: string } | null> {
    try {
      const response = await api.post(`/clinic/${clinicId}/stripe-onboarding/refresh-link`);
      return response.data?.data || null;
    } catch (error) {
      console.error('Failed to refresh Stripe onboarding link:', error);
      return null;
    }
  },

  async startStripeOnboarding(clinicId: string): Promise<{ onboardingUrl: string; accountId: string } | null> {
    const response = await api.post(`/clinic/${clinicId}/stripe-onboarding`);
    return response.data?.data || null;
  },


  async getClinicTransactions(clinicId: string, params: any): Promise<any> {
    const queryParams = new URLSearchParams(params);
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params.start_date) queryParams.set('start_date', params.start_date);
    if (params.end_date) queryParams.set('end_date', params.end_date);

    const response = await api.get(`/clinic/${clinicId}/transactions?${queryParams.toString()}`);
    return response.data;
  },

  async getClinicSettlements(clinicId: string): Promise<any> {
    const response = await api.get(`/clinic/${clinicId}/settlements`);
    return response.data;
  },

  async getSettlementTransfers(
    clinicId: string,
    settlementId: string,
    params?: { page?: number; limit?: number }
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await api.get(
      `/clinic/${clinicId}/settlements/${settlementId}/transfers?${queryParams.toString()}`
    );
    return response.data;
  },

  async exportTransactions(
    clinicId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      format?: 'csv' | 'excel' | 'json';
      status_filter?: 'APPROVED' | 'PENDING' | 'FAILED';
    }
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.format) queryParams.append('format', params.format);
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter);

    const response = await api.post(
      `/payments/clinics/${clinicId}/transactions/export?${queryParams.toString()}`
    );
    return response.data;
  },

  async exportSettlements(
    clinicId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      format?: 'csv' | 'excel' | 'json';
      status_filter?: 'APPROVED' | 'PENDING' | 'FAILED';
    }
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.format) queryParams.append('format', params.format);
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter);

    const response = await api.post(
      `/payments/clinics/${clinicId}/settlements/export?${queryParams.toString()}`
    );
    return response.data;
  },

  async getClinicPaymentOverview(
    clinicId: string,
    params?: {
      start_date?: string;
      end_date?: string;
    }
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);

    const response = await api.get(`/clinic/${clinicId}/payment-overview?${queryParams.toString()}`);
    return response.data;
  },

  async getTransactionDetails(transactionId: string): Promise<any> {
    const response = await api.get(`/payment-reports/transactions/${transactionId}`);
    return response.data;
  },

  async getSettlementDetails(settlementId: string): Promise<any> {
    const response = await api.get(`/payment-reports/settlements/${settlementId}`);
    return response.data;
  },

  // ============================================
  // (payout account management removed — handled natively via Stripe Connect dashboard)
  // ============================================
};
